import { IFilterAllData, ListOfKeysFilterAllData } from 'src/typings/filter';
import { NumberTransformationConfig } from 'src/components/profileComponents/Marketplace/types';
import { isFinite } from 'lodash';
import { useSelector } from 'react-redux';
import { getLocale } from 'src/store/locale/selectors';
import { isFormattedNumber } from 'src/helpers/isNumeric';

const fieldsToCompare = [
  ListOfKeysFilterAllData.placementTotalPrice,
  ListOfKeysFilterAllData.placementTotalPriceAlternative,
  ListOfKeysFilterAllData.placementPricePerSqM,
  ListOfKeysFilterAllData.placementPricePerSqMAlternative,
];

export const numberSeparatorFieldsExclude = [
  ListOfKeysFilterAllData.placementNumeration,
  'numeration',
  ListOfKeysFilterAllData.placementFloor,
  'floors.numeration',
  'house.numeration',
];

export const UseNumberFormat = (groupSeparator?: string, decimalSeparator?: string) => {
  const localeData = useSelector(getLocale);
  const defaultGroupSeparator: string = ' ';
  const defaultDecimalSeparator: string = '.';

  const numberSeparator = (value: string, placementProperty?: string, customFieldsExcludeList?: string[], useDefaultSeparators: boolean = false) => {
    const list = customFieldsExcludeList || numberSeparatorFieldsExclude;
    if ((placementProperty && list.includes(placementProperty as ListOfKeysFilterAllData)) || !isFormattedNumber(value)) {
      return value;
    }
    return formatNumberWithSeparator(
      value,
      (useDefaultSeparators ? defaultGroupSeparator : localeData?.groupSeparator || groupSeparator || defaultGroupSeparator),
      (useDefaultSeparators ? defaultDecimalSeparator : localeData?.decimalSeparator || decimalSeparator || defaultDecimalSeparator),
    );
  };

  const formatNumberWithSeparator = (value: string, groupSeparationChar: string, decimalSeparationChar: string = '.') => {
    const [integerPart, decimalPart] = value.toString().split('.');
    const groupedInteger = integerPart
      .replace(/[\s,]+/g, '')
      .replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparationChar);
    return groupedInteger.length && decimalSeparationChar !== undefined && decimalPart !== undefined ? `${groupedInteger}${decimalSeparationChar}${decimalPart}` : groupedInteger;
  };

  const replaceFraction = (value: string) => value.toString().replace(/\b(\d+)\.00\b/g, '$1');

  const compareAllArrayValuesAfterDecimal = (data: string[], searchValue?: string): boolean => {
    const defaultSearchValue = '.00';
    const compareValue = searchValue || defaultSearchValue;
    return data.some((item) => item?.length && item.slice(item.length - compareValue.length) !== compareValue);
  };

  const roundIfDecimalsIsZero = (data: IFilterAllData[], searchKeysArray: ListOfKeysFilterAllData[], useDefaultSeparators: boolean = false) => {
    let result = data;
    let fieldsToCompareParams = {};
    searchKeysArray.forEach((searchKey) => {
      if (!compareAllArrayValuesAfterDecimal(data.map((item) => item[searchKey] || ''))) {
        fieldsToCompareParams = {
          ...fieldsToCompareParams,
          [searchKey]: true,
        };
        result = result.map((item) => ({ ...item, [searchKey]: item[searchKey] ? numberSeparator(String(Math.round(item[searchKey])), searchKey, undefined, useDefaultSeparators) : item[searchKey] }));
      } else {
        fieldsToCompareParams = {
          ...fieldsToCompareParams,
          [searchKey]: false,
        };
        result = result.map((item) => ({ ...item, [searchKey]: item[searchKey] ? numberSeparator(String(item[searchKey]), searchKey, undefined, useDefaultSeparators) : item[searchKey] }));
      }
    });
    return {
      data: result,
      fieldsToCompareParams,
    };
  };

  const getNumberTransformedValue = (value: string, config: Partial<NumberTransformationConfig> | undefined) => {
    let result = value;
    if (isFinite(Number(result))) {
      if (config && config?.precision !== -1) {
        result = Number(result).toFixed(config.precision);
      }
      if (config && config?.thousandsSeparator?.length) {
        result = formatNumberWithSeparator(String(result), config.thousandsSeparator);
      }
    }
    return result;
  };

  return {
    numberSeparator,
    formatNumberWithSeparator,
    replaceFraction,
    compareAllArrayValuesAfterDecimal,
    roundIfDecimalsIsZero,
    getNumberTransformedValue,
    localeData,
    fieldsToCompare,
    numberSeparatorFieldsExclude,
  };
};
